import { Injectable, Output, EventEmitter } from "@angular/core";
import { Platform } from "@ionic/angular";
import { HubConnection, HubConnectionState, HubConnectionBuilder, HttpTransportType, LogLevel } from "@microsoft/signalr";
import { HttpService } from "src/app/core/data/service/http.service";
import { ServiceHelper } from "src/app/core/helper/services.helper";
import { Dictionary, Utilities } from "src/app/core/helper/util";
import { AuthenticationService } from "src/app/core/modules/auth-core/data/service/authentication.service";
import { Auction } from "../schema/auction";
import { ItemType, AuctionItem } from "../schema/auction-item";
import { AuctionState } from "../schema/auction-state";
import { Bid } from "../schema/bid";
import { ChatMessage } from "../schema/chat-message";
import { Guest } from "../schema/guest";
import { IAuctionEvent, AuctionEventType } from "../schema/interfaces";
import { PushMessage } from "../schema/push-message";
import { Raffle } from "../schema/raffle";
import { Sponsor } from "../schema/sponsor";
import { NotificationService } from "./notification.service";
import { OneSignalService } from "./onesignal.service";
import { SponsorService } from "./sponsor.service";
import { HttpClient } from "@angular/common/http";
import { ThemeService } from "./theme.service";
import { AppSettings } from "src/app/core/configs/app.settings";
import { PageType } from "../schema/cbs/page";
import { StripeConnectService } from './stripe-connect.service';
import { Capacitor } from "@capacitor/core";


@Injectable({
  providedIn: 'root'
})
export class AuctionService extends HttpService {

  @Output() auctionEvent: EventEmitter<IAuctionEvent> = new EventEmitter<IAuctionEvent>();

  private userAuctionState: AuctionState;
  private auction: Auction;
  private registeredAuctionId = 0;
  private pollInterval: any;
  private stateInterval: any;
  private endVerified = false;
  private isVerifying = false;
  private actingGuest: Guest;
  private connection: HubConnection;
  private busy = false;
  private didDisconnect = false;

  // Some caching
  private createdAuctions: Auction[] = [];
  private adminAuctions: Auction[] = [];

  // set on load of auction so can be used in TokenInterceptor
  static communityId: number;
  static auctionId: number;

  constructor(
    private notificationService: NotificationService,
    private onesignalService: OneSignalService,
    private platform: Platform,
    private themeService: ThemeService,
    private sponsorSvc: SponsorService,
    authService: AuthenticationService, // we import instead of using ServiceHelper as this is instantiated before ServiceHelper
    private stripeSvc: StripeConnectService,
    http: HttpClient) {

    super(http);

    // Set the api endpoint, build default headers, and bind the events
    this.endpoint = `${this.apiURL}/auctions`;

    authService.loginStateChanged.subscribe(async state => {
      if (state === 0) {
        clearInterval(this.pollInterval);
        this.didDisconnect = true;
        if (this.signalrConnected) {
          await this.connection.stop();
        }
        this.auction = null;
        this.themeService.resetColors();
        this.userAuctionState = null;
      }
    });
  }

  get currentState() { return this.userAuctionState; }
  get currentAuction() { return this.auction; }
  get isAuctionAdmin() { return this.auction != null && this.auction.canEdit === true; }
  get inAuction() { return (this.auction != null); }
  get inPastAuction() { return this.auction != null && this.auction.hasEnded === true; }
  get inLiveAuction() { return this.auction != null && this.auction.isLive === true; }
  get auctionEnded() { return this.endVerified; }
  get actingAs() { return this.actingGuest; }
  get canParticipateEmailValid() { return (this.auction.requireActivatedEmail === false || ServiceHelper.authSvc.profile.emailConfirmed === true); }
  get canParticipateHasTicket() {
    if (this.auction?.requireTicket == false) {
      return true;
    } else if (this.actingAs == null) {
      return this.auction.ticketCount > 0;
    } else if (this.isAuctionAdmin) {
      return this.actingAs.ticketCount > 0
    }
    return false;
  }

  // Hub connection states
  get signalrConnected() { return (this.connection != null && this.connection.state === HubConnectionState.Connected); }
  get isBusy() { return this.busy; }


  async save(auction: Auction) {
    const isNew = (auction.auctionId == null);

    let ep = this.endpoint;

    // new uses temp endpoint
    if (isNew) {
      ep += '/create';
    }

    // If saving an existing auction, make sure we properly set the keys as they are not returned from the server
    auction = new Auction(await super.save(auction, ep, 'json'));

    // If new, connect to the hub so we get updates, and set the auction as the active auction
    if (isNew === true) {
      this.auction = auction;
      if (this.auction.brandingEnabled && this.auction.brandingColor != null) {
        this.themeService.applyColor('theme', this.auction.brandingColor);
      } else {
        this.themeService.resetColors();
      }

      this.userAuctionState = new AuctionState(ServiceHelper.authSvc.profile.userId, this.auction);
      // running start all the special things we need to ensure our auction is always accurate
      this.connectToAuction();

      AuctionService.communityId = this.auction.communityId;
      AuctionService.auctionId = this.auction.auctionId;

      this.auctionEvent.emit({
        id: auction.auctionId,
        event: AuctionEventType.AuctionUpdated
      });
      this.endVerified = this.auction.hasEnded;
    } else {
      // ensure all keys set based on response from server
      this.auction.payPalSecretKey = auction.payPalSecretKey;
      this.auction.braintreeSecretKey = auction.braintreeSecretKey;
      this.auction.braintreeMerchantId = auction.braintreeMerchantId;
      this.auction.stripeSecretKey = auction.stripeSecretKey;
      this.auction.stripeWebhookSecret = auction.stripeWebhookSecret;
      this.auction.squareAccessToken = auction.squareAccessToken;
      this.auction.squareWebhookSecret = auction.squareWebhookSecret;
      this.auction.stripeAccountId = auction.stripeAccountId;
    }

    return this.auction; // To complete the await
  }

  async endAuction() {
    if (this.isAuctionAdmin === false) {
      throw new Error('You must be administrator to end the auction');
    }
    // Emit that the auction end has changed
    this.auctionEvent.emit({ event: AuctionEventType.AuctionEndChanged });

    return this.deleteItem(this.auction.auctionId, `${this.endpoint}/end`);
  }

  async getRaffle(code: string) {
    let rrApiEndpoint = AppSettings.rrApiUrl;

    rrApiEndpoint += `/RaffleGuest/code/${code}`
    try {
      return new Raffle(await super.fetch(rrApiEndpoint));
    } catch (e) {
      console.log(`Error getting raffle: ${e}`);
      throw new Error(e);
    }
  }

  async deleteAuction() {
    if (this.isAuctionAdmin === false) {
      throw new Error('You must be administrator to delete the auction');
    }
    await this.deleteItem(this.auction.auctionId, `${this.endpoint}`);

    // Emit that we left the auction
    this.auction = null;
    this.themeService.resetColors();
    this.userAuctionState = null;;
    this.auctionEvent.emit({ event: AuctionEventType.AuctionDeleted });
  }

  async reloadAuction() {
    try {
      if (this.auction == null) {
        throw 'Auction not loaded, cannot continue';
      } else if (Utilities.stringEmpty(this.auction.accessCode)) {
        throw 'Auction code not set, cannot continue';
      }
      return await this.getAuctionByCode(this.auction.accessCode, true);
    } catch (error) {
      console.error('Error reloading auction!', error);
      throw error;
    }
  }

  async getAuctionByCode(code: string, setActive: boolean = true) {
    if (Utilities.stringEmpty(code)) {
      return;
    }

    try {
      const auction = new Auction(await this.fetch(`${this.endpoint}/bycode/${code}`));
      if (setActive === true) {
        // If the auctionId is null, then we got no auction
        if (auction.auctionId == null) {
          // If we had an auction, then clear it and let the ap know we aren't in an auction
          this.clearCurrentAuction();
          AuctionService.communityId = undefined;
          AuctionService.auctionId = undefined;
        } else {
          // This may be a re-load so if we get it and the auction is ended, then set ended as verified
          if (auction.hasEnded) {
            this.endVerified = true;
          }

          // Set the client up with this auction as active
          await this.auctionUpdated(auction);
          this.connectToAuction();
        }

        if (auction.canEdit) {
          // load banking info to get warning for all admins. Maybe API could only pass
          // down status and not banking info unless creator? not sure it matters...
          // no need to await, but here we load stripe connect info for auction so
          // we know if we should show that banking requires attention or not
          this.stripeSvc.getAuctionBankInfo()
            .catch(err => console.log('Error getting auction bank info', err));
        }
      }
      return auction;
    } catch (error) {
      console.error('Error getting auction for code ' + code, error);
      throw error;
    } finally {
      this.isVerifying = false;
    }
  }

  async getAuctionById(id: number) {
    const auction = new Auction(await super.getById(id));
    return auction;
  }

  async fetchCreatedAuctions() {
    try {
      // We cache created auctions as they likely won't change much (at all) during a session
      if (this.createdAuctions.length === 0) {
        this.createdAuctions = (await this.fetch(`${this.endpoint}/mine`) as Auction[]).map(m => new Auction(m));
      }
      return this.createdAuctions;
    } catch (error) {
      console.error('Error fetching created auctions', error);
      throw error;
    }
  }

  async fetchAdminAuctions() {
    try {
      if (this.adminAuctions.length === 0) {
        this.adminAuctions = (await this.fetch(`${this.endpoint}/adminauctions`) as Auction[]).map(m => new Auction(m));
      }
      return this.adminAuctions;
    } catch (error) {
      console.error('Error fetching auctions user was admin of', error);
      throw error;
    }
  }

  async getInvitedAuctionUsers() {

    if (this.auction == null) {
      console.error('No auction loaded, cannot get invited users');
      return;
    }

    try {
      const a = (await this.fetch(`${this.endpoint}/invitedusers/${this.auction.auctionId}`) as Guest[]).map(m => new Guest(m));
      return a;
    } catch (error) {
      console.error('Error fetch invited auction users', error);
      throw error;
    }
  }

  async saveWatchItem(productId: number) {
    try {
      this.userAuctionState.toggleItemFollowing(productId);
      let watchItems = this.userAuctionState.watchedItems.toString();
      watchItems = watchItems.replace('[', '');
      watchItems = watchItems.replace(']', '');
      this.auction.watchedItems = watchItems;
      this.userAuctionState = new AuctionState(ServiceHelper.authSvc.profile.userId, this.auction);
      // this.userAuctionState.refresh();

      return this.post({ items: watchItems }, `${this.endpoint}/watch/${this.currentAuction.auctionId}`)
    } catch (error) {
      throw error;
    }
  }

  inviteUsers(emailAddresses: string[]) {
    return this.post({ emailAddresses }, `${this.endpoint}/${this.auction.auctionId}/invite`, 'text');
  }

  joinAuction(accessCode: string) {
    console.log('Request to join auction: ' + accessCode);
    return this.post(null, `${this.endpoint}/join/${accessCode}`, 'text');
  }

  enterAuction(auctionId: number) {
    console.log('Enter auction for user.');
    return this.post(undefined, `${this.endpoint}/enter/${auctionId}`, 'text');
  }

  async exitCurrentAuction() {
    await this.post(undefined, `${this.endpoint}/exit`, 'text');
    // Emit that we left the auction
    this.clearCurrentAuction();
    return;
  }

  async leaveAuction(auctionId: number) {
    await this.deleteItem(auctionId, `${this.endpoint}/leave`, 'text');
    if (this.auction != null && this.auction.auctionId === auctionId) {
      this.clearCurrentAuction();
    }
  }

  async clearCurrentAuction() {
    if (this.auction != null) {
      // Stop acting as
      if (this.actingGuest != null) {
        this.actingGuest = null;
      }

      // If we were in an auction, we should unregister
      await this.unregisterWithHub();
      clearInterval(this.pollInterval);

      // Emit that we left the auction
      this.auction = null;
      this.themeService.resetColors();
      this.userAuctionState = null;
      this.auctionEvent.emit({ event: AuctionEventType.NoAuction });
    }
  }

  async getExportUrl(type: string) {
    console.log('Request export token for auction.');
    const token = await this.fetch(`${this.endpoint}/${this.currentAuction.auctionId}/exportcode`);
    return `${this.endpoint}/export/${type}/${token}`;
  }

  async setActingAs(guest: Guest) {
    // Ensure we are auction admin. The call before this runs this check too
    if (this.isAuctionAdmin === true) {
      console.log('Setting acting as.', guest);
      this.actingGuest = guest;
      // Now we need to fetch the max bids for the guest and update our items
      this.getGuestState(guest.userId);
      if (guest.userId > 0) {
        this.userAuctionState = new AuctionState(guest.userId, this.auction);
      }
    }
  }

  async clearActingAs() {
    if (this.actingGuest != null) {
      this.actingGuest = null;
      this.getGuestState(ServiceHelper.authSvc.profile.userId);
    }
  }


  async getGuestState(userId: number) {
    // Only admins can run this mkay
    if (userId != ServiceHelper.authSvc.profile.userId && this.isAuctionAdmin === false) {
      return;
    }

    // Clear the current max bids and auth state
    for (const productId of Object.keys(this.auction.products)) {
      this.auction.products[productId].maxBid = 0;
    }

    this.auction.hasPreAuth = false;
    this.userAuctionState = new AuctionState(userId, this.auction);

    // Only fetch if user id has value
    if (userId > 0) {
      try {

        // determine the url we will use to get the state
        let stateUrl = `${this.endpoint}/${this.auction.auctionId}/`;
        if (userId != ServiceHelper.authSvc.profile.userId) {
          stateUrl += `guest/${userId}/`;
        }
        stateUrl += 'state';

        const state = await this.fetch(stateUrl) as IGuestState;
        // update the items to have the correct max bids
        for (const productId of Object.keys(state.maxBids)) {
          const prod = this.auction.products[productId];
          if (prod.minimumBid < state.maxBids[productId]) {
            prod.maxBid = state.maxBids[productId];
          }
        }

        // Ensure the auction has the right pre-auth state and watched items
        this.auction.hasPreAuth = state.hasPreAuth;
        this.auction.watchedItems = state.watchedItems;

        this.userAuctionState.refresh();

      } catch (error) {
        console.error('Error getting guest state', error);
        // something broke sadly
      }
    }
  }

  getTimeRemaining() {
    return (this.auction == null) ? null : this.auction.timeRemaining;
  }

  private async connectToHub() {
    if (this.connection == null) {
      // Create the connection if one doesn't exist
      // this.connection = new HubConnectionBuilder()
      //   .withUrl(this.appSettings.hubURL)
      //   .configureLogging(LogLevel.Information)
      //   .build();

      this.connection = new HubConnectionBuilder()
        .withUrl(AppSettings.hubURL, {
          skipNegotiation: true,
          transport: HttpTransportType.WebSockets
        })
        .configureLogging(LogLevel.Information)
        .build();


      // Setup the events
      this.connection.onclose(error => this.hubClosed(error));

      // Auction events
      this.connection.on('AuctionUpdated', data => this.auctionUpdated(data));
      this.connection.on('AuctionEnded', data => this.onAuctionEnded(data));
      this.connection.on('SponsorUpdated', data => this.updateSponsor(data));
      this.connection.on('Broadcast', data => this.newBroadcast(data));

      // Item events
      this.connection.on('ProductUpdated', data => this.updateItem(data));
      this.connection.on('ProductPurchased', data => this.addBid(data));
      this.connection.on('BidAdded', data => this.addBid(data));
      this.connection.on('PurchaseRemoved', data => this.removeBid(data));
      this.connection.on('BidRemoved', data => this.removeBid(data));

      // User events
      this.connection.on('Activate', data => ServiceHelper.authSvc.profile.emailConfirmed = true);

      // Message events
      this.connection.on('Message', data => {
        const msg = new ChatMessage(data);
        this.auctionEvent.emit({
          id: msg.threadId,
          event: AuctionEventType.NewMessage,
          payload: msg
        });
      });

      // Payment/PreAuth events
      this.connection.on('PreAuthComplete', data => {
        this.auctionEvent.emit({
          event: AuctionEventType.PreAuthComplete,
          payload: data
        });
      });

      this.connection.on('TestPaymentComplete', data => {
        this.auctionEvent.emit({
          event: AuctionEventType.TestPaymentComplete,
          payload: data
        });
      });

    }
    if (this.signalrConnected === false && this.busy === false) {
      try {
        // Start the connection if we aren't already connected
        this.busy = true;
        await this.connection.start();
        this.didDisconnect = false;
      } catch (error) {
        console.error('Error connecting to auction hub.', error);
        throw error;
      } finally {
        this.busy = false;
      }
    }
  }

  private hubClosed(error: Error) {
    console.error('Socket connection closed.', error);
    this.registeredAuctionId = 0;
    if (this.didDisconnect === false) {
      this.runReconnect();
    }
  }

  private runReconnect() {
    console.log('Attempting to reconnect to auction hub.');
    let retries = 5;
    const reconnectInt = setInterval(async () => {
      if (retries > 0) {
        console.log('Attempting to reconnect to auction hub. Retries left:', retries);
        try {
          await this.registerWithHub();
          if (this.signalrConnected === true) {
            clearInterval(reconnectInt);
            console.log('Reconnected to auction hub, requesting auction state.');
            await this.getAuctionByCode(this.currentAuction.accessCode, true); // force refresh
          }
          retries -= 1;
        } catch (error) {
          console.error('Error reconnecting to auction hub. Retries remaining: ', retries, error);
        }
      } else {
        console.error('Error reconnecting to auction hub. Retries 0');
        clearInterval(reconnectInt);
      }
    }, 5000);
  }

  private async registerWithHub() {
    // Okay, we ensured that we have an auction and aren't registered for it yet
    if (this.auction != null && this.registeredAuctionId !== this.auction.auctionId) {
      try {
        if (this.signalrConnected === false) {
          await this.connectToHub();
        }
        if (this.signalrConnected === true) {
          // First lets unregister with anything we were registered with
          await this.unregisterWithHub();
          // Then lets register
          await this.connection.send('RegisterWithHub', {
            userId: ServiceHelper.authSvc.profile.userId,
            auctionId: this.auction.auctionId
          });
          this.registeredAuctionId = this.auction.auctionId;
        } else {
          console.error('Unable to connect to socket.');
        }
      } catch (error) {
        console.error('Error running register with hub. Error', error);
        this.registeredAuctionId = 0; // couldn't run the register so obviously it's 0
      }
    }
  }

  private async unregisterWithHub() {
    // If the hub is connected. Lets run the diconnect command
    if (this.signalrConnected === true) {
      await this.connection.send('DisconnectFromHub', {
        userId: ServiceHelper.authSvc.profile.userId,
        auctionId: this.registeredAuctionId
      });
    }
    // Set that we are not registered with any auction
    this.registeredAuctionId = 0;
  }

  private onAuctionEnded(auction: Auction) {
    // Create the local notification saying the auction ended and notify the users
    console.log('Auction is over:', auction);
    this.notificationService.create(auction.auctionId, 'Auction has Ended', 'Check your bids!', new Date());
    this.auctionUpdated(auction);
  }

  private auctionUpdated(auction: Auction) {
    const isThisAuction = (this.auction != null && this.auction.auctionId === auction.auctionId);

    if (auction.deleted) {
      // Emit that the auction was deleted
      this.auctionEvent.emit({
        id: this.auction.auctionId,
        event: AuctionEventType.AuctionDeleted
      });

      // If the current auction is the deleted one.. clear it
      if (isThisAuction) {
        this.auction = null;
        this.themeService.resetColors();
        this.userAuctionState = null;
        this.auctionEvent.emit({ event: AuctionEventType.AuctionUpdated });
      }

    } else {

      if (isThisAuction) {

        // If the updated auction has a smaller access than the updated one
        // then what we got was a generic auction so we need to make sure to
        // preserve the current state

        if (this.auction.access > auction.access) {
          auction.access = this.auction.access;

          // Preserve user-specific things
          auction.hasPreAuth = this.auction.hasPreAuth;
          auction.ticketCount = this.auction.ticketCount;
          auction.watchedItems = this.auction.watchedItems;

          // If we have any secret keys, then lets make sure we keep them
          auction.payPalSecretKey = this.auction.payPalSecretKey;
          auction.braintreeSecretKey = this.auction.braintreeSecretKey;
          auction.braintreeMerchantId = this.auction.braintreeMerchantId;
          auction.stripeSecretKey = this.auction.stripeSecretKey;
          auction.stripeWebhookSecret = this.auction.stripeWebhookSecret;
          auction.squareAccessToken = this.auction.squareAccessToken;
          auction.squareWebhookSecret = this.auction.squareWebhookSecret;

          // Keep any fees, caps, etc
          auction.fee = Math.min(this.auction.fee, auction.fee);
          auction.feeCap = this.auction.feeCap ?? auction.feeCap;
          auction.totalFeeCollected = Math.max(this.auction.totalFeeCollected, auction.totalFeeCollected);
          auction.totalTaxCollected = Math.max(this.auction.totalTaxCollected, auction.totalTaxCollected);
        }

      }

      this.auction = new Auction(auction);
      if (this.auction.brandingEnabled && this.auction.brandingColor != null) {
        this.themeService.applyColor('theme', this.auction.brandingColor);
      } else {
        this.themeService.resetColors();
      }

      // Make sure the auction state is setup and up-to-date
      if (this.userAuctionState == null) {
        this.userAuctionState = new AuctionState(ServiceHelper.authSvc.profile.userId, this.auction);
      } else {
        // If the user auction state is already setup, then lets just refresh it
        if (this.userAuctionState.userId == null) {
          this.userAuctionState.userId = ServiceHelper.authSvc.profile.userId;
        }
        this.userAuctionState.auction = this.auction;
        this.userAuctionState.refresh();
      }

      // If we need to mask the bidder names due to update.. lets do that
      if (this.auction.hideBidderName && this.auction.canEdit === false) {
        const myUserId = (this.actingAs != null) ? this.actingAs.userId : ServiceHelper.authSvc.profile.userId;
        for (var key in this.auction.products) {
          const product = this.auction.products[key];
          product.bids.forEach(bid => {
            // Check if we are supposed to mask the bid or not
            if (bid.userId !== myUserId) {
              bid.displayName = `${bid.displayName.substring(0, 1)}******`;
              if (bid.email != null) {
                bid.email = `${bid.email.substring(0, 1)}******`;
                bid.firstName = `${bid.firstName.substring(0, 1)}******`;
                bid.lastName = `${bid.lastName.substring(0, 1)}******`;
              }
            }
          });
        }
      }

      // We verify from the server if the auction has ended
      this.endVerified = this.auction.hasEnded;
      // setup local notifications, tell the app the auction was updated
      this.createTimeWarnings();
      this.auctionEvent.emit({
        id: this.auction.auctionId,
        event: AuctionEventType.AuctionUpdated
      });

      // set communityId of current auction
      AuctionService.communityId = this.auction.communityId;
      AuctionService.auctionId = this.auction.auctionId;
    }
  }

  private registerWithOnesignal() {
    if (Capacitor.isNativePlatform() === true) {
      // Build up the tags to send
      const tags = { userId: `${ServiceHelper.authSvc.profile.userId}`, version: AppSettings.version };
      if (this.auction != null) {
        tags['auction-' + this.auction.auctionId] = 'true';
      }
      // Send them
      this.onesignalService.updateTags(tags);
    }
  }

  private createTimeWarnings() {
    if (Capacitor.isNativePlatform()) {
      this.notificationService.clearAll();
      const endDate = new Date(this.auction.endDateUtc).getTime();
      this.notificationService.create(
        5,
        '5 Minutes left in auction!',
        '5 Minutes left in auction',
        new Date(endDate - 5 * 60000)
      );
      this.notificationService.create(
        15,
        '15 Minutes left in auction!',
        '15 Minutes left in auction',
        new Date(endDate - 15 * 60000)
      );
      this.notificationService.create(
        30,
        '30 Minutes left in auction!',
        '30 Minutes left in auction',
        new Date(endDate - 30 * 60000)
      );
    }
  }

  private async removeBid(bid: Bid) {

    // First remove the item from the auction
    const item = this.auction.products[bid.productId];
    const hadBidToRemove = item.removeBid(bid);
    if (hadBidToRemove === false) {
      return;
    }

    item.bidCount -= 1;

    // Then refresh the auction state
    this.userAuctionState.refresh();

    // Check if type is 1, if it is then check if we are "now" winning
    const myId = ServiceHelper.authSvc.profile.userId;
    if (item.type === ItemType.BidItem &&
      this.userAuctionState.isWinningItem(bid.productId) === true &&
      bid.userId !== myId) {

      const oldBidAmount = +(bid.amount.replace(this.auction.currencySymbol, ''));
      if (oldBidAmount > item.currentBid) {
        const payload = {
          item: item.productId,
          title: 'Winning!',
          message: `You are now winning: ${item.title}`
        };
        this.auctionEvent.emit({
          id: item.productId,
          event: AuctionEventType.WinStateChanged,
          payload
        });
      }
    }

    // If it was my bid, clear the max-bid
    if (bid.userId === myId) {
      item.maxBid = 0;
    }

    // Trigger the bid updated
    this.auctionEvent.emit({
      id: item.productId,
      event: AuctionEventType.BidDeleted,
      payload: item
    });
  }

  private async addBid(bid: Bid) {

    // ensure the bid is cast properly
    bid = new Bid(bid);

    // First lets store the bid on the item
    const item = this.auction.products[bid.productId];

    // console.log('addBid', bid, item);

    // Store these so we know if we are now the winner (as the guest)
    const myUserId = (this.actingAs != null) ? this.actingAs.userId : ServiceHelper.authSvc.profile.userId;
    const hadWinningBid = (item.winningUserId != null && item.winningUserId === myUserId);


    item.bidCount += 1 + bid.autoBids.length;

    // Check if we are supposed to mask the bid or not
    if (this.auction.hideBidderName === true &&
      bid.userId !== myUserId &&
      this.auction.canEdit === false) {
      // Since we aren't admin, hide the bidder info
      bid.displayName = `${bid.displayName.substring(0, 1)}******`;
      if (bid.email != null) {
        bid.email = `${bid.email.substring(0, 1)}******`;
        bid.firstName = `${bid.firstName.substring(0, 1)}******`;
        bid.lastName = `${bid.lastName.substring(0, 1)}******`;
      }
    }

    // Check if we are acting as a guest without a userId, that placed a bid
    if (this.actingAs != null && this.actingAs.userId == null) {
      // Test for it being the root bid
      if (bid.email === this.actingAs.contact) {
        this.actingAs.userId = bid.userId;
      } else if (bid.autoBids.length > 0) {
        // There were autobids, lets try those
        const guestBid = bid.autoBids.find(m => m.email === this.actingAs.contact);
        if (guestBid != null) {
          this.actingAs.userId = guestBid.userId;
        }
      }
      // If we found a bid and set a user ID, refetch the max bids
      if (this.actingAs.userId > 0) {
        this.getGuestState(this.actingAs.userId);
      }
    }

    // Then add the bids which will update the item
    bid.autoBids.map(m => item.addBid(m, m.userId === myUserId));
    bid.autoBids = [];

    // If it's my bid, the type isn't blind, or we are admin.. add the bid
    if ((bid.userId === ServiceHelper.authSvc.profile.userId) || (item.type === ItemType.BlindBid == false) || this.isAuctionAdmin) {
      item.addBid(bid, bid.userId === myUserId);
    }


    // ensure we only show most recent 20 bids
    if (item.bids.length > 20 && item.isBidOn) {
      // grab only the first 20 bids
      item.bids = item.bids.slice(0, 20);
    }

    // Remove the max bid from the item if it's not valid anymore
    if (item.currentBid >= item.maxBid) {
      item.maxBid = 0;
    }

    // Get the current win state
    this.userAuctionState.refresh();

    // Let the user know they were outbid
    if (item.type === ItemType.BidItem && hadWinningBid === true && this.userAuctionState.isWinningItem(bid.productId) === false) {
      const payload = {
        item: item.productId,
        title: 'Outbid!',
        message: `You have been outbid on: ${item.title} by: ${bid.displayName}`
      };
      this.auctionEvent.emit({
        id: item.productId,
        event: AuctionEventType.WinStateChanged,
        payload
      });
    }

    // The new end for the item/auction
    let newEnd = new Date(bid.auctionEnd).valueOf();

    // Record the current end time and the new end time for the auction
    let currEnd = new Date(this.auction.endDateUtc).valueOf();

    // If the current end is sooner than the new end, update to be new end
    if (currEnd < newEnd) {
      this.auction.endDateUtc = bid.auctionEnd;
    }

    // Now lets do the item if the item end as well
    currEnd = new Date(item.closeAt).valueOf();
    newEnd = new Date(bid.itemEnd).valueOf();

    if (currEnd < newEnd) {
      // Update the item to have 1 less extensions and the new end
      item.closeAt = bid.itemEnd;
      item.extensionCount -= 1;
    }

    // Show a toast saying that the item has triggered an extension
    const diffMs = (newEnd - currEnd);
    const diffMins = Math.round(((diffMs % 86400000) % 3600000) / 60000);
    if (diffMins > 0) {
      this.auctionEvent.emit({
        event: AuctionEventType.AuctionEndChanged,
        payload: `The bidding war is on! ${item.title} is extended by ${diffMins} minute${diffMins > 1 ? 's' : ''}!`,
        id: item.productId
      });
    }

    // Trigger the bid updated
    this.auctionEvent.emit({
      id: item.productId,
      event: AuctionEventType.BidAdded,
      payload: item
    });
  }

  private async updateSponsor(sponsor: Sponsor) {
    sponsor = new Sponsor(sponsor);
    if (sponsor.deleted === true) {
      // console.log('Sponsor was removed from auction.', sponsor);
      delete this.auction.sponsors[sponsor.sponsorId];
    } else {
      // console.log('Sponsor was updated. New sponsor object:', sponsor);
      // this.auction.sponsors[sponsor.sponsorId] = sponsor;
      // using this cuz the tier doesn't update when we save it
      const getSponsors = await this.sponsorSvc.fetchSponsorById(sponsor.sponsorId);
      this.auction.sponsors[sponsor.sponsorId] = getSponsors;
    }
    this.auctionEvent.emit({
      id: sponsor.sponsorId,
      event: AuctionEventType.SponsorUpdated,
      payload: sponsor
    });
  }

  private async updateItem(item: AuctionItem) {

    // this is a CBS Donation product, don't treat like auction item
    if (item['pgType'] == PageType.Donation) { return; }

    try {
      item = new AuctionItem(item);
      const evPayload = {
        id: item.productId,
        event: AuctionEventType.ItemUpdated,
        payload: item as any
      };

      if (item.deleted === true) {
        // console.log('Item was removed from auction.', item);
        delete this.auction.products[item.productId];
      } else {
        // The product is new. If the auction has not ended. Let the users know the item was added
        if (this.auction.hasEnded === false && this.auction.products[item.productId] == null) {
          const payload = { item: item.productId, title: 'New Item', message: `New item added to auction: ${item.title}`, code: item.code };
          evPayload.event = AuctionEventType.ItemAdded;
          evPayload.payload = payload;
        } else if (item.type === ItemType.BlindBid) {
          const myId = (this.actingAs != null) ? this.actingAs.userId : ServiceHelper.authSvc.profile.userId;
          const myBids = item.bids.filter(m => m.userId === myId);
          const maxBid = myBids.reduce((prev, current) => (prev > current.value) ? prev : current.value, 0);
          item.currentBid = maxBid;
          item.currentBidStr = `${this.auction.currencySymbol}${maxBid}`;
          if (this.isAuctionAdmin === false) {
            item.bids = myBids;
          }
        }

        // Now replace (or add) the item
        // console.log('Item was updated. New item object:', item);
        this.auction.products[item.productId] = item;
      }

      this.auctionEvent.emit(evPayload);
    } catch (error) {
      console.error('Error updating item.', error);
    }
  }

  private newBroadcast(pushMessage: PushMessage) {
    this.auction.pushMessages.push(pushMessage);
    this.auctionEvent.emit({
      event: AuctionEventType.NewBroadcast,
      payload: pushMessage
    });
  }


  private async connectToAuction() {
    console.log('Connecting/Establishing auction heartbeat');

    // Begin basic setup
    this.startPollLoop();
    this.startAuctionStateInterval();
    this.registerWithOnesignal();

    // Register with the hub
    try {
      await this.registerWithHub();
    } catch (error) {
      console.error('Get active auction failed to connect to hub.', error);
    }
  }

  private startPollLoop() {
    if (this.pollInterval == null) {
      this.pollInterval = setInterval(() => {
        // If signalr isn't connect but should be then we will fetch the auction details
        if (this.signalrConnected === false && this.auction.auctionId > 0) {
          console.log('SignalR not connected and auction is still live. Polling for updated auction.');
          try {
            this.reloadAuction().catch(err => {
              console.error(`Error polling for updated auction. Error ${err}`);
            });
          } catch (error) {
            console.error(`Error polling for updated auction. Error ${error}`);
          }
        }
      }, 30000);
    }
  }

  private startAuctionStateInterval() {
    if (this.stateInterval != null) {
      return; // already started
    }

    // Start a timer if we aren't already running
    this.stateInterval = setInterval(() => {

      // If no auction. Clear the interval
      if (this.auction == null) {
        clearInterval(this.stateInterval);
        this.stateInterval = null;
        return;
      }

      // trigger the update which will update times and such
      this.auction.update();
      this.userAuctionState.update();

      if (this.auction.hasEnded === true) {
        if (this.signalrConnected === true) {
          // if connected to signalr, assume the local object is up-to-date
          this.endVerified = true;
        } else if (this.endVerified === false && this.signalrConnected === false && this.isVerifying === false) {
          // Okay, the auction has ended. Lets verify it actually is if we aren't connected
          this.isVerifying = true;
          this.reloadAuction().catch(() => {
            // If we fail to reload the auction, we can't assume it has ended
            this.endVerified = false;
            this.isVerifying = false;
          });
        }
      }
    }, 1000);
  }









  protected isUpdate(entity: Auction) {
    return (entity.auctionId != null);
  }

  protected getUpdateUrl(entity: Auction, endpoint: string = this.endpoint) {
    return `${endpoint}/${entity.auctionId}`;
  }


}

interface IGuestState {
  hasPreAuth: boolean;
  maxBids: Dictionary<number>;
  watchedItems: string;
}
